import { createFileRoute } from '@tanstack/react-router'
import { type ColDef } from 'ag-grid-community'
import { useMemo } from 'react'

import type { LoyaltyCard, LoyaltyCardStatusType } from '@/api/admin-loyalty-card-types'
import { preloadListLoyaltyCards, useListLoyaltyCards } from '@/api/react-query'
import { Container, ContainerHeader } from '@/components/base/container'
import { SearchField } from '@/components/base/search-field'
import { StatusCell, type StatusColor } from '@/components/base/status-cell'
import { SingleColumnPage } from '@/components/page-layout'
import { DEFAULT_PAGE_SIZE, Pagination } from '@/components/table/pagination'
import { Table, useServerSort } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'
import { paginatedSearchWithSort } from '@/utils/route-utilities'
export const Route = createFileRoute('/_authenticated/_general/loyalty-cards/')({
	component: LoyaltyCardsPage,
	validateSearch: paginatedSearchWithSort(['name', 'cardNumber', 'status']),
	loaderDeps: ({ search: { limit, offset, q, sortBy, sortOrder } }) => ({
		limit,
		offset,
		q,
		sortBy,
		sortOrder
	}),
	loader: async ({ context, deps: { limit, offset, q, sortBy, sortOrder } }) =>
		await preloadListLoyaltyCards(context, {
			query: {
				limit,
				offset,
				q,
				sortBy,
				sortOrder
			}
		}),
	staticData: {
		crumb: ({ context: { L } }) => L.LoyaltyCards.Title()
	}
})

function LoyaltyCardsPage() {
	const { L } = useI18n()
	const { limit = DEFAULT_PAGE_SIZE, offset, q, sortBy, sortOrder } = Route.useSearch()
	const serverSort = useServerSort(['name', 'cardNumber', 'status'])

	const cards = useListLoyaltyCards({
		limit,
		offset,
		q,
		sortBy,
		sortOrder
	})

	const columnDefs = useMemo<ColDef<LoyaltyCard>[]>(
		() => [
			{
				headerName: L.LoyaltyCards.Column.Name(),
				field: 'name',
				minWidth: 150,
				flex: 2
			},
			{
				headerName: L.LoyaltyCards.Column.CardNumber(),
				field: 'cardNumber',
				minWidth: 140,
				flex: 2
			},
			{
				headerName: L.LoyaltyCards.Column.Customer(),
				field: 'customer',
				minWidth: 160,
				flex: 2,
				valueGetter: ({ data }) => formatCustomerName(data?.customer, L.Customers.Anonymous())
			},
			{
				headerName: L.LoyaltyCards.Column.AvailablePoints(),
				field: 'availablePoints',
				minWidth: 120,
				flex: 1,
				valueFormatter: ({ value }) => (typeof value === 'number' ? value.toLocaleString() : '-')
			},
			{
				headerName: L.LoyaltyCards.Column.Status(),
				field: 'status',
				minWidth: 100,
				flex: 1,
				cellRenderer: ({ value }: { value: LoyaltyCardStatusType }) => (
					<StatusCell color={getLoyaltyCardStatusColor(value)}>{L.Enum.CardStatus(value)}</StatusCell>
				)
			}
		],
		[L]
	)

	return (
		<SingleColumnPage>
			<Container className='flex flex-1 flex-col overflow-hidden'>
				<ContainerHeader heading={L.LoyaltyCards.Title()} rightSide={<SearchField />} />
				<div className='min-w-0 flex-1 overflow-x-auto'>
					<Table<LoyaltyCard>
						className='w-full min-w-[600px] flex-1'
						data={cards.data?.cards}
						columnUtilities={{}}
						columnDefs={columnDefs}
						serverSort={serverSort}
						animateRows
						suppressCellFocus
						rowToRouterConfig={card => ({
							to: '/loyalty-cards/$cardUuid',
							params: { cardUuid: card.uuid }
						})}
					/>
				</div>
				<div className='px-3 py-2 sm:px-6 sm:py-4'>
					<Pagination totalCount={cards.data?.count} />
				</div>
			</Container>
		</SingleColumnPage>
	)
}

const formatCustomerName = (customer: LoyaltyCard['customer'] | undefined, anonymousFallback: string): string => {
	if (!customer) return '-'
	const name = [customer.firstName, customer.lastName].filter(Boolean).join(' ')
	return name || customer.email || anonymousFallback
}

const getLoyaltyCardStatusColor = (status: LoyaltyCardStatusType): StatusColor => {
	switch (status) {
		case 'ACTIVE':
			return 'green'
		case 'INACTIVE':
			return 'grey'
		case 'BLOCKED':
			return 'orange'
		default:
			return 'grey'
	}
}
